import AsyncStorage from "@react-native-async-storage/async-storage";

let owner: string | null = null;
let generation = 0;
const globalKeys = new Set(["@user", "@token", "@timestamp30Days", "@legacyStorageOwner"]);
export const accountIdentity = (user: any) => String(user.openHouseUuid || user.uuid || user.id);
export const captureAccount = () => {
  const captured = generation;
  return () => {
    if (captured !== generation) throw new Error("Account changed; saved work remains queued");
  };
};
export const selectStorageAccount = async (user: any) => {
  generation++;
  owner = user ? accountIdentity(user) : null;
  // Pin legacy ownership before a later login overwrites @user.
  let legacyOwner = await AsyncStorage.getItem("@legacyStorageOwner");
  if (legacyOwner === null) {
    const previous = JSON.parse((await AsyncStorage.getItem("@user")) || "null");
    legacyOwner = previous ? accountIdentity(previous) : "unattributed";
    await AsyncStorage.setItem("@legacyStorageOwner", legacyOwner);
  }
  if (!user) return;
  // Only the persisted authenticated owner can claim pre-scoping data.
  // Unattributed legacy queues remain on disk for recovery, never auto-replayed.
  if (legacyOwner !== owner) return;
  const selected = owner;
  const assertAccount = captureAccount();
  const marker = `account:${encodeURIComponent(selected)}:@legacyMigrationComplete`;
  if (await AsyncStorage.getItem(marker) === "true") return;
  assertAccount();
  const keys = (await AsyncStorage.getAllKeys()).filter(key =>
    !globalKeys.has(key) && !key.startsWith("account:") && key !== "@pendingCheckInAttendees");
  const destination = (key: string) => `account:${encodeURIComponent(selected)}:${key}`;
  const values = new Map(await AsyncStorage.multiGet([...keys, ...keys.map(destination)]));
  assertAccount();
  const writes: [string, string][] = [];
  const removals: string[] = [];
  for (const key of keys) {
    const value = values.get(key);
    const existing = values.get(destination(key));
    if (value == null) continue;
    if (existing == null) writes.push([destination(key), value]);
    // An interrupted migration may have copied without removing the original.
    // Preserve conflicting legacy data for recovery.
    if (existing == null || existing === value) removals.push(key);
  }
  if (writes.length) await AsyncStorage.multiSet(writes);
  assertAccount();
  if (removals.length) await AsyncStorage.multiRemove(removals);
  assertAccount();
  await AsyncStorage.setItem(marker, "true");
};
const keyFor = (key: string) => {
  if (globalKeys.has(key)) return key;
  // Public check-in links have their own capability-bound queue.
  if (key === "@pendingCheckInAttendees") return key;
  if (!owner) throw new Error("Sign in before accessing saved account data");
  return `account:${encodeURIComponent(owner)}:${key}`;
};
export default {
  getItem: async (key: string) => {
    const assertAccount = captureAccount();
    const value = await AsyncStorage.getItem(keyFor(key));
    assertAccount();
    return value;
  },
  setItem: (key: string, value: string) => AsyncStorage.setItem(keyFor(key), value),
  removeItem: (key: string) => AsyncStorage.removeItem(keyFor(key)),
  getAllKeys: () => AsyncStorage.getAllKeys(),
};

// Bind a multi-step operation before its first await. Each call checks the
// original session, and storage keys are resolved before native I/O begins.
export const captureAccountStorage = () => {
  const assertAccount = captureAccount();
  return {
    assertAccount,
    getItem: async (key: string) => {
      assertAccount();
      const value = await AsyncStorage.getItem(keyFor(key));
      assertAccount();
      return value;
    },
    setItem: async (key: string, value: string) => {
      assertAccount();
      await AsyncStorage.setItem(keyFor(key), value);
      assertAccount();
    },
    removeItem: async (key: string) => {
      assertAccount();
      await AsyncStorage.removeItem(keyFor(key));
      assertAccount();
    },
  };
};
